import { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { availableLaboratoriesSubscriptionAtom } from '../store/subscription.atoms'
import { GetAvailableLaboratoriesSubscription } from '../generated/urql.client'
import ReactSelect, { components } from 'react-select'
import { MdLocationPin } from 'react-icons/md'
import { Blue300, Red800 } from '../lib/colors'

type CollectionPoint = NonNullable<
  GetAvailableLaboratoriesSubscription['available_laboratory']
>[number]['collectionPoints'][number]

type CollectionPointSelectorProps = {
  value?: number
  onChange: (collectionPointId: number, collectionPoint: CollectionPoint) => void
  error?: string
  appearance?: 'input' | 'button'
  className?: string
  showLabel?: boolean
  testIds?: number[]
}

export const CollectionPointSelector = ({
  value,
  onChange,
  error,
  appearance = 'button',
  className,
  showLabel = true,
  testIds,
}: CollectionPointSelectorProps) => {
  const [
    {
      data: availableLaboratoriesData,
      fetching: availableLaboratoriesFetching,
    },
  ] = useAtomValue(availableLaboratoriesSubscriptionAtom)

  const collectionPoints = useMemo(() => {
    if (!availableLaboratoriesData?.available_laboratory) return []

    const allPoints = availableLaboratoriesData.available_laboratory.flatMap(
      (lab) => {
        if (testIds && testIds.length > 0) {
          const labTestIds = new Set(lab.laboratoryTests.map((lt) => lt.testId))
          const supportsAll = testIds.every((id) => labTestIds.has(id))
          if (!supportsAll) return []
        }
        return lab.collectionPoints || []
      }
    )

    return allPoints.sort((a, b) => {
      const distanceA = a.distance ?? Infinity
      const distanceB = b.distance ?? Infinity
      return distanceA - distanceB
    })
  }, [availableLaboratoriesData, testIds])

  const selectedPoint = collectionPoints.find((p) => p.id === value)

  return (
    <div className="flex flex-col gap-2">
      {showLabel && (
        <label className="text-sm font-bold text-blue-700">
          Wybierz punkt pobrań
        </label>
      )}
      <ReactSelect
        className={className}
        classNames={{
          control: ({ hasValue }) => {
            return appearance === 'button'
              ? `${
                  hasValue ? '!border-green-700' : '!border-red-700'
                } !border-2 !rounded-xl h-12 !shadow-none !cursor-pointer`
              : '!shadow-none !rounded-none !border-t-0 !border-l-0 !border-r-0 !border-b !border-b-border !border-solid !border-opacity-50 !p-2.5 !bg-transparent !text-lg'
          },
          singleValue: () =>
            '!text-blue-700 font-bold !whitespace-normal leading-none',
          valueContainer: () => '!flex !flex-row-reverse',
          indicatorSeparator: () => 'hidden',
          dropdownIndicator: () => '!text-blue-600',
          option: ({ isFocused, isSelected }) =>
            `!text-blue-700 ${isFocused ? '!bg-blue-200' : ''} ${
              isSelected ? 'font-bold !bg-white' : ''
            }`,
          menu: () => '!w-auto',
          input: () => '',
          placeholder: ({ hasValue }) =>
            hasValue ? '!text-blue-700' : '!font-bold !text-red-800',
        }}
        noOptionsMessage={() => 'Brak dostępnych punktów pobrań'}
        loadingMessage={() => 'Ładowanie punktów pobrań...'}
        isLoading={availableLaboratoriesFetching}
        isOptionSelected={(option, selectValue) =>
          !!selectValue.find((o) => o.value.id === option.value.id)
        }
        components={{
          ValueContainer: ({ children, ...props }) => (
            <components.ValueContainer {...props}>
              {children}
              <MdLocationPin
                size={24}
                color={props.hasValue ? Blue300 : Red800}
              />
            </components.ValueContainer>
          ),
        }}
        placeholder="Wybierz punkt pobrań"
        value={
          selectedPoint
            ? {
                label: (
                  <div>
                    <p>{selectedPoint.name}</p>
                    <p className="text-xs whitespace-nowrap text-black">
                      {selectedPoint.address}, {selectedPoint.postalCode}{' '}
                      {selectedPoint.city}
                      {selectedPoint.distance !== null &&
                      selectedPoint.distance !== undefined
                        ? ` • ${(selectedPoint.distance / 1000).toFixed(1)} km`
                        : ''}
                    </p>
                  </div>
                ),
                value: selectedPoint,
              }
            : null
        }
        onChange={(option) => {
          if (option) {
            onChange(option.value.id, option.value)
          }
        }}
        options={collectionPoints.map((point) => ({
          value: point,
          label: (
            <div>
              <p>{point.name}</p>
              <p className="text-xs whitespace-nowrap text-black">
                {point.address}, {point.postalCode} {point.city}
                {point.distance !== null && point.distance !== undefined
                  ? ` • ${(point.distance / 1000).toFixed(1)} km`
                  : ''}
              </p>
            </div>
          ),
        }))}
        hideSelectedOptions
      />
      {error && <p className="text-red-700 text-xs">{error}</p>}
    </div>
  )
}

